import { toast } from "@/components/common/Toast"
import type { ExcelEditorHandle } from "@/components/ExcelEditor"
import { AnsweringContextDialog } from "@/components/feature/Modals/AnsweringContextDialog/AnsweringContextDialog"
import { ContinueLaterModal } from "@/components/feature/Modals/ContinueLaterModal/ContinueLaterModal"
import { DeleteQuestionnaireModal } from "@/components/feature/Modals/DeleteQuestionnaireModal/DeleteQuestionnaireModal"
import {
  readCompanyProfilePreference,
  SelectDocumentsDialog,
} from "@/components/feature/Modals/SelectDocumentsDialog/SelectDocumentsDialog"
import { PERMISSIONS } from "@/constants/permissions"
import { usePermissionGuard } from "@/hooks/usePermissionGuard"
import { useQuestionnaireAutofill } from "@/hooks/useQuestionnaireAutofill/useQuestionnaireAutofill"
import { useQuestionnaireStatusNavigation } from "@/hooks/useQuestionnaireStatusNavigation/useQuestionnaireStatusNavigation"
import { useScrollToAnswerByQuery } from "@/hooks/useScrollToAnswerByQuery/useScrollToAnswerByQuery"
import { useTextAreaStates } from "@/hooks/useTextAreaStates/useTextAreaStates"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { ErrorPage } from "@/pages/ErrorPage"
import { isQuestionnaireNotFound } from "@/services/api/questionnaireQueryPolicy"
import {
  useEditResponsePair,
  useGetQuestionnaireAnsweringContext,
  useGetQuestionnaireFilterConfig,
  useUpdateQuestionnaireStatus,
} from "@/services/api/questionnaireService"
import { TextAreaStateAction } from "@/types/hooks/useTextAreaStates"
import {
  EditResponsePairArgs,
  Questionnaire,
  QuestionnaireStatus,
} from "@/types/questionnaire/models"
import { isExcelFileName } from "@/utils/fileUtils"
import { isLimitError } from "@/utils/limitErrors"
import { reduceNumTotalQuestions } from "@/utils/questionnaire/reduceQuestions"
import { WorkbookInstance } from "@fortune-sheet/react"
import type { UseMutateFunction } from "@tanstack/react-query"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router"

import { BreadcrumbsNavigation } from "@/components/common/BreadcrumbsNavigation/BreadcrumbsNavigation"
import { Button } from "@/components/common/Button/Button"
import {
  SplitView,
  SplitViewSection,
} from "@/components/common/SplitView/SplitView"
import { Layout } from "@/components/layout"
import { Button as ShadCnButton } from "@/components/ui/button"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import {
  ONBOARDING_CELEBRATE_STEP_ID,
  ONBOARDING_DEMO_ANSWERS_JSON_PATH,
  ONBOARDING_DEMO_QUESTIONNAIRE_ID,
  ONBOARDING_DONE_STEP_ID,
  ONBOARDING_FIRST_ANSWER_SOURCE_STEP_ID,
  ONBOARDING_FIRST_ANSWER_STEP_ID,
  useOnboarding,
  useOnboardingQuestionnaireData,
  useOnboardingQuestionnaireDocument,
} from "@/features/onboarding"
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs/useBreadcrumbs"
import { AnalyticsEvent, trackEvent } from "@/services/analytics/amplitude"
import { IconCheck, IconNotes } from "@tabler/icons-react"
import { EDIT_RESPONSE_PAIR_KEY } from "./constants"
import { ExcelPreviewPanel } from "./ExcelPreviewPanel"
import { QASection } from "./QASection"
import { useExcelCellSync } from "./useExcelCellSync"

/**
 * Page component for viewing and editing Excel-based questionnaire answers.
 * Displays a split view with Excel preview on the left and Q&A section on the right.
 */
export const ExcelAnswersPage: React.FC = () => {
  const { translate } = useTranslation()
  const excelIntelligenceEnabled = useFeatureFlag("EXCEL_INTELLIGENCE")
  const taggingFlowEnabled = useFeatureFlag("KB_TAGGING_FLOW")
  const answeringContextEnabled = useFeatureFlag("ANSWERING_CONTEXT")
  const { questionnaireId } = useParams<Record<string, string | undefined>>()
  const navigate = useNavigate()
  const workbookRef = useRef<WorkbookInstance | null>(null)
  // The new editor's handle, used instead of workbookRef under EXCEL_EDITOR_V2.
  const editorRef = useRef<ExcelEditorHandle>(null)
  const { breadcrumbs } = useBreadcrumbs()
  const {
    handleHighlightedAction,
    getCurrentTour,
    currentStepIndex,
    isActive,
  } = useOnboarding()
  const currentTour = getCurrentTour()
  const currentStepId = currentTour?.steps?.[currentStepIndex]?.id
  const isOnboardingFirstAnswerStep =
    isActive && currentStepId === ONBOARDING_FIRST_ANSWER_STEP_ID
  const isOnboardingFirstAnswerSourceStep =
    isActive && currentStepId === ONBOARDING_FIRST_ANSWER_SOURCE_STEP_ID
  const isOnboardingDoneStep =
    isActive && currentStepId === ONBOARDING_DONE_STEP_ID
  const ANSWER_TOUR_STEP_IDS = useMemo(
    () => [
      ONBOARDING_CELEBRATE_STEP_ID,
      ONBOARDING_FIRST_ANSWER_STEP_ID,
      ONBOARDING_FIRST_ANSWER_SOURCE_STEP_ID,
      ONBOARDING_DONE_STEP_ID,
    ],
    [],
  )
  const isOnboardingAnswersTourStep =
    isActive && !!currentStepId && ANSWER_TOUR_STEP_IDS.includes(currentStepId)

  const shouldLockAnswersScroll =
    isOnboardingFirstAnswerStep ||
    isOnboardingFirstAnswerSourceStep ||
    isOnboardingDoneStep
  const shouldDisableFirstAnswerSources = isOnboardingFirstAnswerSourceStep
  const shouldDisableFirstAnswerTextResize = isOnboardingFirstAnswerStep
  const shouldHighlightFirstAnswer = isOnboardingAnswersTourStep

  // Modal states
  const [isContinueLaterModalOpen, setIsContinueLaterModalOpen] =
    useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isSelectDocumentsOpen, setIsSelectDocumentsOpen] = useState(false)
  const [isAnsweringContextOpen, setIsAnsweringContextOpen] = useState(false)

  // Questionnaire data fetching
  const {
    data: questionnaire,
    refetch: refetchQuestionnaire,
    isPending: isQuestionnairePending,
    isFetched: isQuestionnaireFetched,
    isError: isQuestionnaireError,
    error: questionnaireError,
    isDemo: isDemoQuestionnaire,
  } = useOnboardingQuestionnaireData(questionnaireId, {
    demoSourcePath: ONBOARDING_DEMO_ANSWERS_JSON_PATH,
  })

  const isDemoFlow = useMemo(() => {
    return (
      isDemoQuestionnaire ||
      questionnaireId === ONBOARDING_DEMO_QUESTIONNAIRE_ID
    )
  }, [isDemoQuestionnaire, questionnaireId])
  const shouldEnableTaggingFlow = taggingFlowEnabled && !isDemoFlow

  const hasGeneratedAnswers = !!questionnaire?.sections.some((section) =>
    section.responsePairs.some((pair) => !!pair.response),
  )

  // Answering context only applies to v2 (ingested) questionnaires; a legacy
  // questionnaire's saved context would never reach the answering pipeline.
  // The answering-context endpoints require QUEST_RUN; viewers without it get neither the
  // button nor the query behind it.
  const { canRun: canRunQuestionnaire } = usePermissionGuard(
    PERMISSIONS.QUEST_RUN,
  )
  const showAnsweringContextButton =
    answeringContextEnabled &&
    canRunQuestionnaire &&
    questionnaire?.ingestionStatus != null

  const { data: answeringContext } = useGetQuestionnaireAnsweringContext(
    questionnaireId,
    showAnsweringContextButton,
  )
  const answeringContextSaved = !!(
    answeringContext?.text || answeringContext?.terms.length
  )

  const { runAutofillWithRedirect, isPending: isAutofillPending } =
    useQuestionnaireAutofill({
      questionnaireId,
      questionnaire,
      navigate,
      refetchQuestionnaire,
    })
  // Guards against a second regenerate firing while the first is still in
  // flight (e.g. two save-success toasts both invoking the regenerate action).
  // A ref, not state, so the check is synchronous on the very first call.
  const isRegeneratingRef = useRef(false)

  // Excel file fetching
  const shouldFetchDocument =
    !!questionnaireId &&
    !!questionnaire &&
    isExcelFileName(questionnaire.fileName) &&
    !isQuestionnaireNotFound(questionnaireError)

  const {
    data: excelFileUrl,
    isPending: isExcelFilePending,
    isFetching: isDocumentFetching,
    isError: isExcelFileError,
  } = useOnboardingQuestionnaireDocument(questionnaireId, shouldFetchDocument, {
    demoDocumentVariant: "answered",
  })

  const { data: questionnaireFilterConfig } = useGetQuestionnaireFilterConfig(
    questionnaireId,
    shouldEnableTaggingFlow &&
      !!questionnaire &&
      !isQuestionnaireNotFound(questionnaireError),
  )

  const selectedDocumentsCount = shouldEnableTaggingFlow
    ? (questionnaireFilterConfig?.matchedCount ?? 0)
    : 0
  const selectedCategoryFilters = shouldEnableTaggingFlow
    ? (questionnaireFilterConfig?.categoryFilters ?? [])
    : []
  const selectedTagIds = selectedCategoryFilters.flatMap(
    (categoryFilter) => categoryFilter.tagIds,
  )
  const excludeExpired = shouldEnableTaggingFlow
    ? (questionnaireFilterConfig?.excludeExpired ?? false)
    : false

  // Status update mutation
  const {
    mutateAsync: updateQuestionnaireStatus,
    isPending: isUpdateStatusPending,
  } = useUpdateQuestionnaireStatus()

  // Navigation and state hooks
  const [, setQuestionnaireStatus] = useQuestionnaireStatusNavigation(
    questionnaire,
    {
      enableAutoRedirect: !isDemoFlow,
    },
  )
  const [numTotalQuestions, setNumTotalQuestions] = useState(0)

  useScrollToAnswerByQuery()

  // Text area state management. mutateResponsePair is wrapped so every answer
  // edit's underlying save is tracked while in flight — flushPendingAnswerEdits
  // (below) awaits all of them before a regenerate is allowed to navigate away.
  const { mutateAsync: mutateResponsePairAsync } = useEditResponsePair()
  const pendingEditSavesRef = useRef<Set<Promise<unknown>>>(new Set())
  // Tracks response pair ids whose most recent answer save rejected. A
  // rejected save is removed from pendingEditSavesRef once it settles, so a
  // later flushPendingAnswerEdits() with no new edit would otherwise see
  // nothing pending and wrongly proceed — losing the displayed-but-unsaved
  // edit. Keeping the id here until a subsequent save for the same pair
  // succeeds keeps flushPendingAnswerEdits() rejecting (and the existing
  // error toast showing) until the edit actually persists.
  const failedEditSavesRef = useRef<Set<string>>(new Set())
  const mutateResponsePair = useCallback<
    UseMutateFunction<Questionnaire, Error, EditResponsePairArgs>
  >(
    (variables, options) => {
      const promise = mutateResponsePairAsync(variables, options)
      pendingEditSavesRef.current.add(promise)
      // Swallow here so a failed save doesn't surface as an unhandled rejection
      // when nobody calls flush(); flushPendingAnswerEdits's await still
      // observes it, and dispatchWithApi's own onError already ran above.
      promise
        .then(() => {
          failedEditSavesRef.current.delete(variables.responsePairId)
        })
        .catch(() => {
          failedEditSavesRef.current.add(variables.responsePairId)
        })
        .finally(() => {
          pendingEditSavesRef.current.delete(promise)
        })
    },
    [mutateResponsePairAsync],
  )
  const [{ textsCount }, updateTextAreaState] = useTextAreaStates(
    questionnaire,
    mutateResponsePair,
    EDIT_RESPONSE_PAIR_KEY,
    false,
  )

  // Registry of per-answer-item flush triggers: each free-text ExcelAnswerItem
  // registers a callback that forces its own pending debounced save to run now
  // (see ExcelAnswerItem's debouncedPersistFreeTextRef). Regenerate calls all
  // of them, then waits for every save they (and any immediate edit) started.
  const pendingItemFlushesRef = useRef<Map<string, () => void>>(new Map())
  const registerAnswerFlush = useCallback((id: string, flush: () => void) => {
    pendingItemFlushesRef.current.set(id, flush)
  }, [])
  const unregisterAnswerFlush = useCallback((id: string) => {
    pendingItemFlushesRef.current.delete(id)
  }, [])
  const flushPendingAnswerEdits = useCallback(async () => {
    pendingItemFlushesRef.current.forEach((flush) => flush())
    await Promise.all(Array.from(pendingEditSavesRef.current))
    if (failedEditSavesRef.current.size > 0) {
      throw new Error("Unsaved answer edits could not be saved")
    }
  }, [])

  // Excel cell synchronization
  const { updateExcelCell, updateDataToApi, wrapAnswerCells } =
    useExcelCellSync({
      workbookRef,
      editorRef,
      updateTextAreaState,
    })
  // Answers the file already holds show whole too, as edited ones do.
  const handlePreviewLoad = () =>
    wrapAnswerCells(
      questionnaire?.sections.flatMap((section) => section.responsePairs) ?? [],
    )
  const shouldBlockAnswerMutations =
    isDemoFlow &&
    (isOnboardingFirstAnswerStep || isOnboardingFirstAnswerSourceStep)

  const handleUpdateDataToApi = useCallback(
    (action: TextAreaStateAction) => {
      if (shouldBlockAnswerMutations) {
        return
      }

      trackEvent(AnalyticsEvent.ANSWER_EDITED, {
        questionnaire_id: questionnaireId,
        edit_type: "excel",
      })

      updateDataToApi(action)
    },
    [shouldBlockAnswerMutations, updateDataToApi, questionnaireId],
  )

  // Redirect non-Excel files to regular answers page
  useEffect(() => {
    if (
      (questionnaire && !isExcelFileName(questionnaire.fileName)) ||
      !excelIntelligenceEnabled
    ) {
      navigate(`/questionnaire/${questionnaireId}/answers`, { replace: true })
    }
  }, [excelIntelligenceEnabled, questionnaire, questionnaireId, navigate])

  // Handle questionnaire data loading
  useEffect(() => {
    if (isQuestionnaireError && !isQuestionnaireNotFound(questionnaireError)) {
      toast.error(translate("QUESTIONNAIRE_QUESTIONS/TOAST_GET_ERROR"))
    } else if (isQuestionnaireFetched && questionnaire) {
      setNumTotalQuestions(reduceNumTotalQuestions(questionnaire))
    }
  }, [isQuestionnaireFetched, questionnaire, isQuestionnaireError, translate])

  useEffect(() => {
    if (!isOnboardingFirstAnswerStep && !isOnboardingFirstAnswerSourceStep) {
      return
    }

    const timer = window.setTimeout(() => {
      document
        .getElementById("first-answer-field")
        ?.scrollIntoView({ behavior: "smooth", block: "center" })
      window.dispatchEvent(new Event("resize"))
    }, 300)

    return () => window.clearTimeout(timer)
  }, [
    isOnboardingFirstAnswerStep,
    isOnboardingFirstAnswerSourceStep,
    questionnaire?.id,
    questionnaire?.sections?.length,
  ])

  // Event handlers
  const handleOpenContinueLaterModal = () => {
    setIsContinueLaterModalOpen(true)
  }

  const handleComplete = async () => {
    if (isDemoFlow) {
      if (isOnboardingDoneStep) {
        handleHighlightedAction("done-answers-button", {
          performNavigation: true,
        })
      } else {
        toast.warning(translate("ONBOARDING/DEMO_ACTION_DISABLED"))
      }
      return
    }

    try {
      const updateStatusResponse = await updateQuestionnaireStatus({
        questionnaireId,
        status: QuestionnaireStatus.Completed,
      })

      await refetchQuestionnaire()
      setQuestionnaireStatus(updateStatusResponse.status)
    } catch (error) {
      // The axios interceptor already showed the limit toast with its upgrade action
      if (!isLimitError(error)) {
        toast.error(translate("QUESTIONNAIRE_ANSWERS/STATUS_UPDATE_ERROR"))
      }
    }
  }

  // Regenerating from the answering-context dialog reuses the same auto-fill
  // trigger as the Questions page; it redirects to the generating-answers
  // route and back, which refetches this questionnaire on remount. Any manual
  // edit still sitting in an answer's debounce must be flushed and persisted
  // first, or the redirect races it and autofill overwrites it.
  const handleRegenerateAnswers = async () => {
    if (!questionnaire || !questionnaireId) {
      toast.error(translate("QUESTIONNAIRE_QUESTIONS/TOAST_AUTOFILL_ERROR"))
      return
    }

    if (isRegeneratingRef.current || isAutofillPending) {
      return
    }

    isRegeneratingRef.current = true
    try {
      await flushPendingAnswerEdits()
      await runAutofillWithRedirect(readCompanyProfilePreference())
    } catch (error) {
      // The axios interceptor already showed the limit toast with its upgrade action
      if (!isLimitError(error)) {
        toast.error(translate("QUESTIONNAIRE_QUESTIONS/TOAST_AUTOFILL_ERROR"))
      }
    } finally {
      isRegeneratingRef.current = false
    }
  }

  if (!questionnaireId) {
    return null
  }

  if (isQuestionnaireNotFound(questionnaireError))
    return <ErrorPage variant="not-found" withLayout />

  return (
    <Layout
      defaultSidebarState={"closed"}
      headerLeftElement={<BreadcrumbsNavigation breadcrumbs={breadcrumbs} />}
      headerRightElement={
        <div className="flex items-center gap-S">
          {selectedDocumentsCount > 0 ? (
            <div className="flex justify-start">
              <ShadCnButton
                variant="link"
                className="h-auto px-0 text-center"
                onClick={() => setIsSelectDocumentsOpen(true)}
              >
                {translate("QUESTIONNAIRE_PREVIEW/DOCUMENTS_USED_BUTTON", {
                  count: selectedDocumentsCount,
                })}
              </ShadCnButton>
            </div>
          ) : null}

          <Button
            variantColor="dark"
            title={translate("QUESTIONNAIRE/CONTINUE_LATER")}
            label={translate("QUESTIONNAIRE/CONTINUE_LATER")}
            onClick={handleOpenContinueLaterModal}
            variantSize="small"
          />
          {showAnsweringContextButton ? (
            <div className="relative">
              <Button
                variantColor="dark"
                title={translate("ANSWERING_CONTEXT/OPEN_BUTTON")}
                label={translate("ANSWERING_CONTEXT/OPEN_BUTTON")}
                onClick={() => setIsAnsweringContextOpen(true)}
                variantSize="small"
                IconLeft={IconNotes}
              />
              {answeringContextSaved ? (
                <span
                  role="img"
                  aria-label={translate("ANSWERING_CONTEXT/SAVED_BADGE")}
                  title={translate("ANSWERING_CONTEXT/SAVED_BADGE")}
                  className="absolute -top-1 -right-1 flex size-3.5 items-center justify-center rounded-full bg-success text-black"
                >
                  <IconCheck size={10} aria-hidden="true" />
                </span>
              ) : null}
            </div>
          ) : null}
          <Button
            elementId="done-answers-button"
            variantColor="yellow"
            title={translate("QUESTIONNAIRE_ANSWERS/DONE")}
            label={translate("QUESTIONNAIRE_ANSWERS/DONE")}
            onClick={handleComplete}
            variantSize="small"
            IconRight={IconCheck}
            isDisabled={
              isUpdateStatusPending ||
              (isDemoFlow ? !isOnboardingDoneStep : false)
            }
          />
        </div>
      }
    >
      {/* Modals */}
      {shouldEnableTaggingFlow ? (
        <SelectDocumentsDialog
          questionnaireId={questionnaireId}
          open={isSelectDocumentsOpen}
          onOpenChange={setIsSelectDocumentsOpen}
          initialSelectedTagIds={selectedTagIds}
          initialHideExpired={excludeExpired}
          readOnly
          readOnlyCategoryFilters={selectedCategoryFilters}
        />
      ) : null}

      {isContinueLaterModalOpen && (
        <ContinueLaterModal
          setIsContinueLaterModalOpen={setIsContinueLaterModalOpen}
        />
      )}

      {isDeleteModalOpen && (
        <DeleteQuestionnaireModal
          questionnaireId={questionnaireId}
          setIsDeleteModalOpen={setIsDeleteModalOpen}
        />
      )}

      {answeringContextEnabled ? (
        <AnsweringContextDialog
          questionnaireId={questionnaireId}
          open={isAnsweringContextOpen}
          onOpenChange={setIsAnsweringContextOpen}
          hasAnswers={hasGeneratedAnswers}
          onRegenerate={() => {
            void handleRegenerateAnswers()
          }}
        />
      ) : null}

      <SplitView>
        <SplitViewSection className="bg-background">
          <ExcelPreviewPanel
            excelFileUrl={excelFileUrl}
            isLoading={isExcelFilePending || isDocumentFetching}
            isError={isExcelFileError}
            workbookRef={workbookRef}
            editorRef={editorRef}
            // Answers show whole: their cells wrap and their rows grow.
            autoRowHeight
            onLoad={handlePreviewLoad}
          />
        </SplitViewSection>

        <SplitViewSection className="bg-dark1 px-M">
          <QASection
            questionnaire={questionnaire}
            isLoading={isQuestionnairePending}
            textsCount={textsCount}
            numTotalQuestions={numTotalQuestions}
            updateExcelCell={updateExcelCell}
            updateDataToApi={handleUpdateDataToApi}
            registerAnswerFlush={registerAnswerFlush}
            unregisterAnswerFlush={unregisterAnswerFlush}
            disableScroll={shouldLockAnswersScroll}
            disableFirstAnswerSources={shouldDisableFirstAnswerSources}
            disableFirstAnswerTextResize={shouldDisableFirstAnswerTextResize}
            highlightFirstAnswer={shouldHighlightFirstAnswer}
          />
        </SplitViewSection>
      </SplitView>
    </Layout>
  )
}
